/* eslint-disable react/no-unstable-nested-components */
import { ApolloError, useApolloClient, useMutation } from "@apollo/client";
import { Mutation } from "@apollo/client/react/components";
import { useGTMDispatch } from "@elgorditosalsero/react-gtm-hook";
import FundamenteiWithText from "components/branding/FundamenteiWithText";
import LogoSizingProps from "components/branding/LogoSizingProps";
import IsolatedLayout from "components/layouts/IsolatedLayout";
import FundamenteiMetadata from "components/seo/FundamenteiMetadata";
import {
  getUtmParametersFromParsedUrlQuery,
  preserveOnlyUtmParameters,
  preserveUtmWithSpecificParameters,
} from "lib/analytics/utm";
import CheckoutUrlMutation from "lib/queries/CheckoutUrlMutation";
import type {
  ICheckoutUrlMutation,
  ICheckoutUrlMutationVariables,
} from "lib/queries/__generated__/CheckoutUrlMutation.generated";
import delayedPromise from "lib/utils/delayedPromise";
import dispatchCustomEvent from "lib/utils/dispatchCustomEvent";
import getCurrentTimeZoneName from "lib/utils/getCurrentTimeZoneName";
import getQueryStringValue from "lib/utils/getQueryStringValue";
import type { NextPage } from "next";
import Link from "next/link";
import Router, { useRouter } from "next/router";
import { useEffect } from "react";
import { useAsyncCallback } from "react-async-hook";
import { FormattedMessage, useIntl } from "react-intl";
import { ICheckoutProduct } from "types/graphqlTypes";
import Auth from "../components/auth/Auth";
import Footer from "../components/auth/Footer";
import GoogleOAuthButton from "../components/auth/GoogleOAuthButton";
import SignUpForm from "../components/auth/SignUpForm";
import Divider from "../components/core/Divider";
import { useAmplitude } from "../lib/amplitude/Amplitude";
import useFlashMessage from "../lib/hooks/useFlashMessage";
import SignUpFromGoogleOAuthAccessToken from "../lib/queries/auth/SignUpFromGoogleOAuthAccessToken";
import SignUpFromRegistrationForm from "../lib/queries/auth/SignUpFromRegistrationForm";
import type {
  ISignUpFromGoogleOAuthAccessTokenMutation,
  ISignUpFromGoogleOAuthAccessTokenMutationVariables,
} from "../lib/queries/auth/__generated__/SignUpFromGoogleOAuthAccessToken.generated";
import type {
  ISignUpFromRegistrationFormMutation,
  ISignUpFromRegistrationFormMutationVariables,
} from "../lib/queries/auth/__generated__/SignUpFromRegistrationForm.generated";

interface SignUpProps {
  checkout: string | null;
  redirectTo: string | null;
  promo: string | null;
}
const SignUp: NextPage<SignUpProps> = function SignUp({ promo, checkout, redirectTo }) {
  const router = useRouter();
  const intl = useIntl();
  const amplitude = useAmplitude();
  const apolloClient = useApolloClient();
  const [errorMessage, setErrorMessage] = useFlashMessage();
  const sendDataToGTM = useGTMDispatch();

  const [checkoutUrl] = useMutation<ICheckoutUrlMutation, ICheckoutUrlMutationVariables>(CheckoutUrlMutation, {});
  const checkoutProduct = useAsyncCallback(async (product: ICheckoutProduct) => {
    const { data } = await checkoutUrl({
      variables: {
        product,
        coupon: promo,
        parameters: null,
      },
    });

    if (data && data.checkoutUrl) {
      window.location.href = data.checkoutUrl;
    }
  });
  const isPromo = promo !== null;

  useEffect(() => {
    apolloClient.resetStore();
  }, []);

  return (
    <IsolatedLayout forcedTheme="light">
      <div className="mx-auto h-full min-h-screen w-screen bg-brand py-16">
        <FundamenteiMetadata
          title={intl.formatMessage({
            defaultMessage: "Cadastre-se | Fundamentei",
            id: "St6ud9",
            description: "Fundamentei Título Metadata",
          })}
        />
        <div className="mx-auto flex max-w-7xl items-center justify-center text-center">
          <div className="grid gap-y-7">
            <div className="text-center">
              <Link
                href={{
                  pathname: "/",
                  query: preserveOnlyUtmParameters(router?.query),
                }}
                passHref={true}
                title="Fundamentei"
                className="flex justify-center text-center"
              >
                <FundamenteiWithText
                  {...LogoSizingProps.FundamenteiWithText.MEDIUM}
                  style={{ display: "inline-flex !important" }}
                  fill="#ffffff"
                />
              </Link>
            </div>

            <div className="min-w-96 space-y-8 rounded-xl border-y border-transparent bg-white p-6 font-aeonik font-normal shadow-sm md:border">
              <div className="space-y-0.5 text-center">
                <h1 className="m-0 text-2xl font-bold">
                  <FormattedMessage defaultMessage="Cadastre-se" id="XWv1zs" description="Sign up page" />
                </h1>

                <p className="text-sm font-normal tracking-wide text-slate-500">
                  <FormattedMessage
                    defaultMessage="Forneça alguns dados para criar sua conta."
                    id="zqBBy4"
                    description="Sign up page"
                  />
                </p>
              </div>

              <div className="grid gap-y-5">
                <Auth>
                  {({ login }) => (
                    <Mutation<ISignUpFromRegistrationFormMutation, ISignUpFromRegistrationFormMutationVariables>
                      mutation={SignUpFromRegistrationForm}
                    >
                      {(signUpFromRegistrationForm) => (
                        <SignUpForm
                          onSubmit={async ({
                            firstName,
                            lastName,
                            email,
                            password,
                            country,
                            turnstileToken,
                            turnstileIdempotencyKey,
                          }) => {
                            try {
                              const { data } = await signUpFromRegistrationForm({
                                variables: {
                                  registrationForm: {
                                    firstName,
                                    lastName,
                                    email,
                                    password,
                                    country,
                                    timeZone: getCurrentTimeZoneName(),
                                    turnstileToken,
                                    turnstileIdempotencyKey,
                                  },
                                  utms: {
                                    utmSource: null,
                                    utmMedium: null,
                                    utmCampaign: null,
                                    utmTerm: null,
                                    utmContent: null,
                                    ...getUtmParametersFromParsedUrlQuery(router.query),
                                  },
                                },
                              });

                              if (data?.signUpFromRegistrationForm) {
                                setErrorMessage("");

                                amplitude.logEvent("Signed up", {
                                  authentication: "credentials",
                                  firstName,
                                  lastName,
                                  email,
                                  ...getUtmParametersFromParsedUrlQuery(router.query),
                                });

                                dispatchCustomEvent("fundamentei:signedUp");

                                // Blocks until the event is sent
                                await new Promise<void>((resolve) => {
                                  sendDataToGTM({
                                    event: "signedUp",
                                    eventTimeout: 1000,
                                    eventCallback: () => {
                                      resolve();
                                    },
                                  });
                                });

                                amplitude.logEvent("Signed in", {
                                  method: "google",
                                  email: data.signUpFromRegistrationForm.email,
                                  ...getUtmParametersFromParsedUrlQuery(router.query),
                                });

                                login(data.signUpFromRegistrationForm.accessToken);
                                await delayedPromise(500);
                                if (
                                  checkout &&
                                  [
                                    ICheckoutProduct.MonthlyRecurringSubscription,
                                    ICheckoutProduct.YearlyRecurringSubscription,
                                  ].includes(checkout as ICheckoutProduct)
                                ) {
                                  checkoutProduct.execute(checkout as ICheckoutProduct);
                                } else if (redirectTo) {
                                  Router.replace(redirectTo);
                                } else {
                                  Router.replace("/");
                                }

                                return true;
                              }

                              const errorMessage = intl.formatMessage({
                                defaultMessage:
                                  "Não foi possível processar seu cadastro. Uma conta com esse mesmo email já existe.",
                                id: "qFXy2N",
                                description: "Sign up page",
                              });

                              setErrorMessage(errorMessage);
                              return false;
                            } catch (err) {
                              if (err instanceof ApolloError) {
                                const topError = (err as ApolloError).graphQLErrors[0];

                                if (
                                  topError &&
                                  (topError.extensions as any) &&
                                  (topError.extensions as any).code === "BAD_USER_INPUT"
                                ) {
                                  setErrorMessage(
                                    intl.formatMessage({
                                      defaultMessage:
                                        "Não foi possível processar o cadastro. Verifique os dados do formulário",
                                      id: "5wd1Lo",
                                      description: "Sign up page",
                                    }),
                                  );
                                  return false;
                                }

                                setErrorMessage(
                                  intl.formatMessage({
                                    defaultMessage: "Não foi possível processar o cadastro",
                                    id: "ec/ZG5",
                                  }),
                                );
                              } else {
                                setErrorMessage(
                                  intl.formatMessage({
                                    defaultMessage: "Whoopz...erro interno do servidor. Tente novamente mais tarde",
                                    id: "SIeI14",
                                    description: "Sign up page",
                                  }),
                                );
                              }

                              return false;
                            }
                          }}
                        />
                      )}
                    </Mutation>
                  )}
                </Auth>

                {errorMessage && (
                  <div className="-mx-6 max-w-96 bg-red-100 px-4 py-3">
                    <p className="m-0 text-center text-sm font-medium tracking-wide text-red-700">{errorMessage}</p>
                  </div>
                )}

                <p className="m-0 text-center text-sm text-slate-500">
                  <FormattedMessage
                    defaultMessage="Já tem uma conta? <link>Faça login</link>"
                    id="YeuG3E"
                    description="Sign up page"
                    values={{
                      link: (text) => (
                        <Link
                          href={{
                            pathname: "/login",
                            query: preserveUtmWithSpecificParameters(router?.query, ["checkout"]),
                          }}
                          passHref={true}
                          className="ml-auto text-sm font-medium text-blue-600 no-underline hover:underline"
                        >
                          {text}
                        </Link>
                      ),
                    }}
                  />
                </p>

                <Divider>
                  <span className="text-sm uppercase tracking-wide text-slate-400">
                    <FormattedMessage defaultMessage="OU" id="zIwCPg" description="Sign up page" />
                  </span>
                </Divider>

                <Auth>
                  {({ login }) => (
                    <Mutation<
                      ISignUpFromGoogleOAuthAccessTokenMutation,
                      ISignUpFromGoogleOAuthAccessTokenMutationVariables
                    >
                      mutation={SignUpFromGoogleOAuthAccessToken}
                      onError={({ networkError, graphQLErrors }) => {
                        if (networkError) {
                          setErrorMessage(
                            intl.formatMessage({
                              defaultMessage: "Não foi possível se comunicar com o servidor.",
                              id: "LZN8Qx",
                              description: "Sign up page",
                            }),
                          );
                          return;
                        }

                        if (graphQLErrors && graphQLErrors.length > 0) {
                          const topError = graphQLErrors[0];

                          if (/Couldn't process your sign up/i.test(topError.message)) {
                            setErrorMessage(
                              intl.formatMessage({
                                defaultMessage:
                                  "Não foi possível processar seu cadastro agora. Tente novamente mais tarde. Desculpe pela inconveniência :(",
                                id: "eENysx",
                                description: "Sign up page",
                              }),
                            );
                            return;
                          }
                        }

                        setErrorMessage(
                          intl.formatMessage({
                            defaultMessage: "Whoopz...erro interno do servidor. Tente novamente mais tarde.",
                            id: "8ygmwD",
                            description: "Sign up page",
                          }),
                        );
                      }}
                      onCompleted={async (data) => {
                        if (data && data.signUpFromGoogleOAuthAccessToken) {
                          if (data.signUpFromGoogleOAuthAccessToken.isRecentlyCreatedUser) {
                            amplitude.logEvent("Signed up", {
                              authentication: "google",
                              email: data.signUpFromGoogleOAuthAccessToken.email,
                              ...getUtmParametersFromParsedUrlQuery(router.query),
                            });

                            dispatchCustomEvent("fundamentei:signedUp");

                            // Blocks until the event is sent
                            await new Promise<void>((resolve) => {
                              sendDataToGTM({
                                event: "signedUp",
                                eventTimeout: 1000,
                                eventCallback: () => {
                                  resolve();
                                },
                              });
                            });
                            // Just to give a little time to the GTM to send the event
                            await delayedPromise(500);
                          }

                          login(data.signUpFromGoogleOAuthAccessToken.accessToken);
                          Router.replace("/");

                          amplitude.logEvent("Signed in", {
                            method: "google",
                            email: data.signUpFromGoogleOAuthAccessToken.email,
                            ...getUtmParametersFromParsedUrlQuery(router.query),
                            promo: isPromo ? promo : undefined,
                          });

                          if (
                            checkout &&
                            [
                              ICheckoutProduct.MonthlyRecurringSubscription,
                              ICheckoutProduct.YearlyRecurringSubscription,
                            ].includes(checkout as ICheckoutProduct)
                          ) {
                            checkoutProduct.execute(checkout as ICheckoutProduct);
                          } else if (redirectTo) {
                            Router.replace(redirectTo);
                          } else {
                            Router.replace("/");
                          }
                        }

                        setErrorMessage("");
                      }}
                    >
                      {(signUpFromGoogleOAuthAccessToken, { loading }) => {
                        return loading ? (
                          <div className="-mx-6 select-none bg-blue-50 px-6 py-3">
                            <p className="m-0 text-center text-xs font-medium tracking-wider text-blue-600">
                              <FormattedMessage defaultMessage="Aguarde...finalizando seu cadastro." id="iO85uZ" />
                            </p>
                          </div>
                        ) : (
                          <GoogleOAuthButton
                            onGetAccessToken={(accessToken) => {
                              setErrorMessage("");

                              signUpFromGoogleOAuthAccessToken({
                                variables: {
                                  accessToken,
                                  timeZone: getCurrentTimeZoneName(),
                                  utms: {
                                    utmSource: null,
                                    utmMedium: null,
                                    utmCampaign: null,
                                    utmTerm: null,
                                    utmContent: null,
                                    ...getUtmParametersFromParsedUrlQuery(router.query),
                                  },
                                },
                              });
                            }}
                          >
                            {intl.formatMessage({
                              defaultMessage: "Cadastrar com o Google",
                              id: "+H6dvc",
                              description: "Sign up page",
                            })}
                          </GoogleOAuthButton>
                        );
                      }}
                    </Mutation>
                  )}
                </Auth>
              </div>
            </div>

            <Footer className="font-medium text-white" />
          </div>
        </div>
      </div>
    </IsolatedLayout>
  );
};

SignUp.getInitialProps = async ({ query }) => ({
  verificationToken: typeof query.verificationToken === "string" ? query.verificationToken : null,
  checkout: getQueryStringValue(query, "checkout"),
  promo: getQueryStringValue(query, "promo"),
  redirectTo: getQueryStringValue(query, "redirectTo"),
});
export default SignUp;
